/**
 * Template-based commentary generation (P1 SC-4).
 *
 * String interpolation from event data. Not AI — replaced by LLM in P2.
 * Multiple templates per event type, selected deterministically from seq
 * to avoid repetition without randomness (randomness breaks replay purity).
 */
import type { BatterHand, Delivery, Event } from "./events.ts";
import type { PlayerId } from "./ids.ts";
import { isDelivery } from "./events.ts";
import { zoneOf } from "./wagon.ts";

type CommentaryNameLookup = (playerId: PlayerId) => string;

const pick = (templates: readonly string[], seq: number): string =>
  templates[seq % templates.length]!;

const dotTemplates = [
  "Defended back to {bowler}.",
  "No run. Good delivery from {bowler}.",
  "Dot ball. {striker} unable to score.",
  "Tight line from {bowler}, {striker} blocks.",
  "{bowler} keeps it tight, no run.",
];

const singleTemplates = [
  "{runs} run. {striker} works it away.",
  "Pushed for {runs}. Batters cross.",
  "{runs} run, rotated off the pads.",
  "Nudged for {runs} by {striker}.",
];

const twoThreeTemplates = [
  "{runs} runs. Good running between the wickets.",
  "{runs} runs taken by {striker}.",
  "Turned away for {runs}.",
];

const fourTemplates = [
  "FOUR! {striker} finds the boundary.",
  "FOUR! Driven beautifully by {striker}.",
  "FOUR! {striker} punches through the covers.",
  "FOUR! Cut away for four by {striker}.",
  "FOUR! {striker} dispatches it to the fence.",
];

const sixTemplates = [
  "SIX! {striker} launches it over the rope!",
  "SIX! Massive hit from {striker}!",
  "SIX! {striker} goes downtown!",
  "SIX! Cleared the boundary with ease.",
  "SIX! That's gone all the way!",
];

/**
 * Used instead of the generic lines when the scorer recorded where the ball went (ADR-0022).
 * The generic fours say "through the covers" about a ball that may have gone to long on, which
 * is exactly the kind of line a player reads out to the person who hit it.
 */
const fourPlaced = [
  "FOUR! {striker} sends it through {zone}.",
  "FOUR! Finds the gap at {zone}, {striker} is away.",
  "FOUR! {striker} picks {zone} and beats the field.",
];

const sixPlaced = [
  "SIX! {striker} clears {zone}!",
  "SIX! Over {zone} and gone.",
  "SIX! {striker} launches it towards {zone}!",
];

const runsPlaced = [
  "{runs} to {zone} by {striker}.",
  "{striker} works it to {zone} for {runs}.",
  "Played to {zone}, {runs} taken.",
];

const wicketTemplates: Record<string, readonly string[]> = {
  bowled: [
    "WICKET! Bowled! {bowler} cleans up {out}.",
    "WICKET! {out} is bowled by {bowler}. Stumps shattered!",
  ],
  caught: [
    "WICKET! Caught! {out} has to walk.",
    "WICKET! {out} caught, {bowler} strikes!",
  ],
  caught_behind: [
    "WICKET! Caught behind! Edge and taken.",
    "WICKET! {out} edges to the keeper.",
  ],
  caught_and_bowled: [
    "WICKET! Caught and bowled! {bowler} takes a sharp return catch.",
    "WICKET! C&B! {bowler} completes it himself.",
  ],
  lbw: [
    "WICKET! LBW! {bowler} traps {out} in front.",
    "WICKET! Plumb LBW. {out} departs.",
  ],
  stumped: [
    "WICKET! Stumped! Quick work behind the stumps.",
    "WICKET! {out} stumped, beaten by the turn.",
  ],
  run_out: [
    "WICKET! Run out! {out} was short of the crease.",
    "WICKET! Direct hit! {out} is run out.",
  ],
  hit_wicket: [
    "WICKET! Hit wicket! {out} dislodges the bails.",
  ],
  mankad: [
    "WICKET! {out} run out at the non-striker's end.",
  ],
  retired_out: [
    "{out} retires out.",
  ],
};

const wideTemplates = [
  "Wide ball. Extra run conceded.",
  "Called wide. {bowler} strays down the leg side.",
];

const noBallTemplates = [
  "No ball! Free hit coming up.",
  "No ball called. {bowler} overstepped.",
];

const milestoneTemplates = {
  fifty: [
    "{striker} reaches FIFTY! Well-played half-century.",
    "FIFTY for {striker}! A fine innings so far.",
  ],
  hundred: [
    "{striker} brings up the CENTURY! Brilliant knock!",
    "A HUNDRED for {striker}! Outstanding innings.",
  ],
};

function fill(template: string, vars: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (_, key) => vars[key] ?? "?");
}

/**
 * Generate commentary for a single delivery.
 */
export function generateBallCommentary(
  event: Event,
  name: CommentaryNameLookup,
  currentRuns?: number,
  /**
   * Which way the striker stands, from the ledger event that brought him in (ADR-0023). The zone
   * of a shot needs it, and a delivery does not carry it. Without it the line is the generic one
   * rather than a zone worked out for an assumed right-hander.
   */
  batterHand?: BatterHand | null,
): string | null {
  if (!isDelivery(event)) return null;

  const delivery = event as Delivery;
  const vars: Record<string, string> = {
    striker: name(delivery.strikerId),
    bowler: name(delivery.bowlerId),
    runs: String(delivery.runs.batter),
    out: delivery.wicket ? name(delivery.wicket.playerOutId) : "",
    // Lower case so it sits mid-sentence: "through cover", "to long on".
    zone:
      delivery.shot === null || batterHand === null || batterHand === undefined
        ? ""
        : zoneOf(delivery.shot.wagonAngle, batterHand).toLowerCase(),
  };
  const placed = vars["zone"] !== "";

  const parts: string[] = [];

  // Extras prefix
  if (delivery.runs.extras.wide > 0) {
    parts.push(fill(pick(wideTemplates, delivery.seq), vars));
  }
  if (delivery.runs.extras.noball > 0) {
    parts.push(fill(pick(noBallTemplates, delivery.seq), vars));
  }

  // Wicket
  if (delivery.wicket) {
    const type = delivery.wicket.type;
    const templates = wicketTemplates[type] ?? [`WICKET! ${type}. {out} departs.`];
    parts.push(fill(pick(templates, delivery.seq), vars));
  } else if (delivery.runs.boundary === "six") {
    parts.push(fill(pick(placed ? sixPlaced : sixTemplates, delivery.seq), vars));
  } else if (delivery.runs.boundary === "four") {
    parts.push(fill(pick(placed ? fourPlaced : fourTemplates, delivery.seq), vars));
  } else if (placed && delivery.runs.batter > 0) {
    parts.push(fill(pick(runsPlaced, delivery.seq), vars));
  } else if (delivery.runs.batter === 0 && delivery.runs.extras.wide === 0 && delivery.runs.extras.noball === 0) {
    parts.push(fill(pick(dotTemplates, delivery.seq), vars));
  } else if (delivery.runs.batter <= 1) {
    parts.push(fill(pick(singleTemplates, delivery.seq), vars));
  } else {
    parts.push(fill(pick(twoThreeTemplates, delivery.seq), vars));
  }

  // Milestones
  if (currentRuns !== undefined) {
    const before = currentRuns - delivery.runs.batter;
    if (before < 100 && currentRuns >= 100) {
      parts.push(fill(pick(milestoneTemplates.hundred, delivery.seq), vars));
    } else if (before < 50 && currentRuns >= 50) {
      parts.push(fill(pick(milestoneTemplates.fifty, delivery.seq), vars));
    }
  }

  return parts.join(" ");
}

/**
 * Generate a match summary for a completed match.
 */
export function generateMatchSummary(summary: {
  readonly winnerName: string | null;
  readonly resultText: string;
  readonly topScorer: { name: string; runs: number; balls: number; notOut: boolean } | null;
  readonly topWicketTaker: { name: string; wickets: number; runs: number } | null;
}): string {
  const parts: string[] = [];

  parts.push(summary.resultText + ".");

  if (summary.topScorer) {
    const s = summary.topScorer;
    parts.push(
      `${s.name} top-scored with ${s.runs}${s.notOut ? "*" : ""} (${s.balls}).`,
    );
  }

  if (summary.topWicketTaker) {
    const w = summary.topWicketTaker;
    parts.push(
      `${w.name} was the pick of the bowlers with ${w.wickets}-${w.runs}.`,
    );
  }

  return parts.join(" ");
}
